import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type AirActionTrackingLocation, useTrackEditedBoard } from '@air/analytics';
import { type Board, type Clip } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PrivateBoardSearch, type PrivateBoardSearchProps } from '~/components/BoardSearch/PrivateBoardSearch';
import { useSingleBoardSearchState } from '~/components/BoardSearch/shared/hooks';
import { ToastLink } from '~/components/UI/ToastLink';
import { usePrivateBoardsSetBoardCoverMutation } from '~/components/v2/hooks/queries/mutations/usePrivateBoardsSetBoardCoverMutation';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { canChangeBoardDetails } from '~/utils/permissions/boardPermissions';

type BoardData = Pick<Board, 'id' | 'title'>;

export type BoardCoverSelectModalProps = {
  clip: Pick<Clip, 'id' | 'assets' | 'workspaceId'>;
  trackLocation: AirActionTrackingLocation;
};

export const BoardCoverSelectModal = memo(
  ({ onClose, trackLocation, clip }: AirModalProps<BoardCoverSelectModalProps>) => {
    const { goToBoardPage } = useGoToPrivateBoardPage();
    const { mutate: setBoardCoverMutation, isPending: isSettingCover } = usePrivateBoardsSetBoardCoverMutation();
    const { trackEditedBoard } = useTrackEditedBoard();
    const { showToast } = useToasts();
    const currentBoard = useSelector(centralizedBoardSelector);
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
    const { getBoardPermissions } = useBoardPermissionsCache();

    const {
      selectedBoard,
      isBoardSelected,
      shouldShowSubBoards,
      onSelectBoard,
      isLibrarySelected,
      shouldShowLibraryBoards,
      onSelectLibrary,
    } = useSingleBoardSearchState({
      initialBoard: currentBoard,
    });

    const onBoardSelectChange: PrivateBoardSearchProps['onBoardSelectChange'] = useCallback(
      ({ board, selected }) => {
        onSelectBoard(selected ? board : null);
      },
      [onSelectBoard],
    );

    const getIsBoardDisabled: Required<PrivateBoardSearchProps>['getIsBoardDisabled'] = useCallback(
      (board) => {
        const boardPermissions = getBoardPermissions(board.id);

        if (!canChangeBoardDetails(boardPermissions)) {
          return {
            isDisabled: true,
            message: 'You do not have permission to edit this board',
          };
        }
      },
      [getBoardPermissions],
    );

    const goToBoard = useCallback(
      (board: BoardData) => {
        goToBoardPage({ board, trackLocation });
      },
      [goToBoardPage, trackLocation],
    );

    const isUpdatingCurrentBoard = useCallback((board: BoardData) => board.id === currentBoard?.id, [currentBoard?.id]);

    const showConfirmationToast = useCallback(
      (board: BoardData) => {
        showToast(
          <>
            {!isUpdatingCurrentBoard(board) ? (
              <ToastLink onClick={() => goToBoard(board)}>{board.title}</ToastLink>
            ) : (
              board.title
            )}
            {` cover has been updated`}
          </>,
        );
      },
      [showToast, isUpdatingCurrentBoard, goToBoard],
    );

    const handleSetCoverClick = useCallback(() => {
      if (!selectedBoard) {
        throw reportErrorToBugsnag({
          error: new Error('No board selected'),
          context: 'BoardCoverSelectModal: Attempted to set cover without selected board',
          metadata: {
            clipId: clip.id,
            workspaceId: clip.workspaceId,
          },
        });
      }

      setBoardCoverMutation(
        {
          boardId: selectedBoard.id,
          workspaceId: clip.workspaceId,
          clipId: clip.id,
          thumbnailUrl: clip.assets?.image,
          // Use default region (full image)
          region: {
            top: 0,
            left: 0,
            width: 100,
            height: 100,
          },
          // Pass the full board data if we're updating the current board
          board: selectedBoard.id === currentBoard?.id ? currentBoard : undefined,
        },
        {
          onSuccess: () => {
            showConfirmationToast(selectedBoard);
            trackEditedBoard({ boardId: selectedBoard.id, location: trackLocation });
            onClose();
          },
          onError: () => {
            showToast('Failed to set board cover. Please try again.');
          },
        },
      );
    }, [
      selectedBoard,
      setBoardCoverMutation,
      clip,
      currentBoard,
      trackLocation,
      showConfirmationToast,
      onClose,
      showToast,
      trackEditedBoard,
    ]);

    const buttonsDisabled = !isBoardSelected || isSettingCover;

    if (showingSubscriptionExpiredModal) {
      return null;
    }
    return (
      <Modal data-testid="SET_BOARD_COVER_MODAL" dangerouslyBypassFocusLock isOpen onDismiss={onClose}>
        <div className="mb-3 flex justify-between">
          <ModalTitle>Set board cover</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </div>
        <ModalDescription className="mb-4">Select a board to set this image as its cover</ModalDescription>
        <PrivateBoardSearch
          isBoardSelected={isBoardSelected}
          onBoardSelectChange={onBoardSelectChange}
          shouldShowSubBoards={shouldShowSubBoards}
          initialBoardId={currentBoard?.id}
          isLibrarySelected={isLibrarySelected}
          shouldShowLibraryBoards={shouldShowLibraryBoards}
          onSelectLibrary={onSelectLibrary}
          shouldExpandLibraryInsteadOfSelect={true}
          getIsBoardDisabled={getIsBoardDisabled}
        />
        <div className="mt-6 flex justify-end gap-3">
          <Button appearance="ghost" color="grey" onClick={onClose} size="large">
            Cancel
          </Button>
          <Button
            appearance="filled"
            color="blue"
            disabled={buttonsDisabled}
            onClick={handleSetCoverClick}
            size="large"
          >
            {isSettingCover ? 'Setting...' : 'Set cover'}
          </Button>
        </div>
      </Modal>
    );
  },
);

BoardCoverSelectModal.displayName = 'BoardCoverSelectModal';
